import { useCallback, useEffect, useState } from 'react'

const STORAGE_EVENT = 'ulty-local-storage-change'

/**
 * Reactive localStorage hook that stays in sync across all instances
 * in the same window by dispatching a custom event on writes.
 */
export const useLocalStorageItem = (key, defaultValue = null) => {
    const read = useCallback(() => {
        const raw = localStorage.getItem(key)
        if (raw === null) return defaultValue
        if (raw.startsWith('"') && raw.endsWith('"')) {
            try {
                const parsed = JSON.parse(raw)
                localStorage.setItem(key, parsed)
                return parsed
            } catch {}
        }
        return raw
    }, [key, defaultValue])

    const [value, setValue] = useState(read)

    useEffect(() => {
        const handler = (e) => {
            if (e.detail?.key === key) {
                setValue(read())
            }
        }
        window.addEventListener(STORAGE_EVENT, handler)
        return () => window.removeEventListener(STORAGE_EVENT, handler)
    }, [key, read])

    const set = useCallback((newValue) => {
        if (newValue === null || newValue === undefined) {
            localStorage.removeItem(key)
        } else {
            localStorage.setItem(key, newValue)
        }
        setValue(newValue ?? null)
        window.dispatchEvent(new CustomEvent(STORAGE_EVENT, { detail: { key } }))
    }, [key])

    return [value, set]
}
